import { RouteName } from "@/apps/merchant3/router/RouteName";
import type { RouteRecordRaw } from "vue-router";
import {
	navigationGuard,
	navigationGuardForCheckOut,
	navigationGuardForClerk,
	navigationGuardForMerchant,
	navigationGuardInStorePayment,
	navigationGuardWelcome,
} from "@/apps/merchant3/helpers/navigationGuards";
import { configurableRoutes } from "@/apps/merchant3/router/configurable-routes";
import { directToCustomerRoutes } from "@/apps/merchant3/router/directToCustomerRoutes";
import { merchant3TranslationGuard } from "@/router/translationGuards";

// Lazy-loaded view components for code splitting
const AskCxCallView = () => import("@/apps/merchant3/views/AskCxCallView.vue");
const AddOnsView = () => import("@/apps/merchant3/views/AddOnsView.vue");
const CartVerificationView = () =>
	import("@/apps/merchant3/views/CartVerificationView.vue");
const ClerkLoginView = () =>
	import("@/apps/merchant3/views/ClerkLoginView.vue");
const ClerkRegistrationView = () =>
	import("@/apps/merchant3/views/ClerkRegistrationView.vue");
const CompleteView = () => import("@/apps/merchant3/views/CompleteView.vue");
const ContractView = () => import("@/apps/merchant3/views/ContractView.vue");
const CreateSalesClerkView = () =>
	import("@/apps/merchant3/views/CreateSalesClerkView.vue");
const CreditCheckPermissionView = () =>
	import(
		"@/apps/merchant3/components/customer-registration/views/CreditCheckPermissionView.vue"
	);
const CustomerContactInfoView = () =>
	import(
		"@/apps/merchant3/components/customer-registration/views/CustomerContactInfoView.vue"
	);
const CustomerRegistrationView = () =>
	import("@/apps/merchant3/views/CustomerRegistrationView.vue");
const DeclinedValidationView = () =>
	import(
		"@/apps/merchant3/components/customer-registration/views/DeclinedValidationView.vue"
	);
const DeviceLoginView = () =>
	import("@/apps/merchant3/views/DeviceLoginView.vue");
const DeviceRepairListView = () =>
	import("@/apps/merchant3/views/DeviceRepairListView.vue");
const DeviceRepairProcessView = () =>
	import("@/apps/merchant3/views/DeviceRepairProcessView.vue");
const EcommerceCashInStoreDownpaymentView = () =>
	import("@/apps/merchant3/views/EcommerceCashInStoreDownpaymentView.vue");
const EcommerceDownPaymentPaidView = () =>
	import("@/apps/merchant3/views/EcommerceDownPaymentPaidView.vue");
const EcommerceFinishedSaleView = () =>
	import("@/apps/merchant3/views/EcommerceFinishedSaleView.vue");
const EcommerceOpenPayDownPaymentView = () =>
	import("@/apps/merchant3/views/EcommerceOpenPayDownPaymentView.vue");
const EcommercePartnerDownPaymentView = () =>
	import("@/apps/merchant3/views/EcommercePartnerDownPaymentView.vue");
const EcommercePixDownPaymentView = () =>
	import("@/apps/merchant3/views/EcommercePixDownPaymentView.vue");
const EcommerceSelectDownpaymentTypeView = () =>
	import("@/apps/merchant3/views/EcommerceSelectDownpaymentTypeView.vue");
const EcommerceSummaryView = () =>
	import("@/apps/merchant3/views/EcommerceSummaryView.vue");
const EcommerceView = () => import("@/apps/merchant3/views/EcommerceView.vue");
const ElectronicDownPaymentStatusView = () =>
	import("@/apps/merchant3/views/ElectronicDownPaymentStatusView.vue");
const ExternalIdUploader = () =>
	import(
		"@/apps/merchant3/components/customer-registration/image-uploader/ExternalImageUploader.vue"
	);
const FaceLoginView = () => import("@/apps/merchant3/views/FaceLoginView.vue");
const FinishSaleCartDetailsView = () =>
	import("@/apps/merchant3/views/FinishSaleCartDetailsView.vue");
const FinishSaleView = () =>
	import("@/apps/merchant3/views/FinishSaleView.vue");
const HelpView = () => import("@/apps/merchant3/views/HelpView.vue");
const IdentificationUploaderView = () =>
	import(
		"@/apps/merchant3/components/customer-registration/views/IdentificationUploaderView.vue"
	);
const ImeiView = () => import("@/apps/merchant3/views/ImeiView.vue");
const IncodeCapture = () =>
	import(
		"@/apps/merchant3/components/customer-registration-v2/Incode/IncodeCapture.vue"
	);
const IncodeFlowView = () =>
	import("@/apps/merchant3/views/IncodeFlowView.vue");
const IncodeIntroductionView = () =>
	import("@/apps/merchant3/views/IncodeIntroductionView.vue");
const LatestMerchantReceiptsView = () =>
	import("@/apps/merchant3/views/LatestMerchantReceiptsView.vue");
const MerchantLoginView = () =>
	import("@/apps/merchant3/views/MerchantLoginView.vue");
const MerchantPaymentReceiptView = () =>
	import("@/apps/merchant3/views/MerchantPaymentReceiptView.vue");
const MerchantRecreatePasswordView = () =>
	import("@/apps/merchant3/views/MerchantRecreatePasswordView.vue");
const MerchantRequestResetPasswordView = () =>
	import("@/apps/merchant3/views/MerchantRequestResetPasswordView.vue");
const MerchantSaleReceiptView = () =>
	import("@/apps/merchant3/views/MerchantSaleReceiptView.vue");
const OffersV2View = () => import("@/apps/merchant3/views/OffersV2View.vue");
const OffersView = () => import("@/apps/merchant3/views/OffersView.vue");
const OpenPayCardUiView = () =>
	import("@/apps/merchant3/views/OpenPayCardUiView.vue");
const PaymentInqueryResultView = () =>
	import("@/apps/merchant3/views/PaymentInqueryResultView.vue");
const PaymentInqueryView = () =>
	import("@/apps/merchant3/views/PaymentInqueryView.vue");
const PaymentsReceiptView = () =>
	import("@/apps/merchant3/views/PaymentReceiptView.vue");
const PostOfferRegistrationView = () =>
	import("@/apps/merchant3/views/PostOfferRegistrationView.vue");
const PreInstallDevicesView = () =>
	import("@/apps/merchant3/views/PreInstallDevicesView.vue");
const PreRegistrationOffersView = () =>
	import("@/apps/merchant3/views/PreRegistrationOffersView.vue");
const PreparingOffersView = () =>
	import("@/apps/merchant3/views/PreparingOffersView.vue");
const QRCodeLoginView = () =>
	import("@/apps/merchant3/views/QRCodeLoginView.vue");
const QRScanLanding = () =>
	import("@/apps/merchant3/components/QRScanLanding.vue");
const QrLoginIncodeCaptureView = () =>
	import("@/apps/merchant3/views/QrLoginIncodeCaptureView.vue");
const ReceiptView = () => import("@/apps/merchant3/views/ReceiptView.vue");
const RejectedOfferEcommerceView = () =>
	import("@/apps/merchant3/views/RejectedOfferEcommerceView.vue");
const SelfOnboardingInstructionsView = () =>
	import("@/apps/merchant3/views/SelfOnboardingInstructionsView.vue");
const SelfOnboardingSummaryView = () =>
	import("@/apps/merchant3/views/SelfOnboardingSummaryView.vue");
const SoftwareInstallFailedView = () =>
	import("@/apps/merchant3/views/SoftwareInstallFailedView.vue");
const SoftwareInstallView = () =>
	import("@/apps/merchant3/views/SoftwareInstallView.vue");
const SpeedMoneyEditView = () =>
	import("@/apps/merchant3/views/SpeedMoneyEditView.vue");
const SpeedMoneyRegisterView = () =>
	import("@/apps/merchant3/views/SpeedMoneyRegisterView.vue");
const SpeedMoneySearchExistingRegistrationView = () =>
	import("@/apps/merchant3/views/SpeedMoneySearchExistingRegistrationView.vue");
const SpeedMoneyView = () =>
	import("@/apps/merchant3/views/SpeedMoneyView.vue");
const SupportedModelsView = () =>
	import("@/apps/merchant3/views/SupportedModelsView.vue");
const TakeASelfieIntroView = () =>
	import("@/apps/merchant3/views/TakeASelfieIntroView.vue");
const TermsAndConditionsView = () =>
	import(
		"@/apps/merchant3/components/customer-registration/views/TermsAndConditionsView.vue"
	);
const WaitValidationView = () =>
	import(
		"@/apps/merchant3/components/customer-registration/views/WaitValidationView.vue"
	);
const WelcomeView = () => import("@/apps/merchant3/views/WelcomeView.vue");
const CustomerDateOfBirthView = () =>
	import(
		"@/apps/merchant3/components/customer-registration/views/CustomerDateOfBirthView.vue"
	);
const CustomerIdNumberView = () =>
	import(
		"@/apps/merchant3/components/customer-registration/views/CustomerIdNumberView.vue"
	);
const PurchaseConfirmationView = () =>
	import("@/apps/merchant3/views/PurchaseConfirmationView.vue");
import { legacyRedirects } from "@/apps/merchant3/router/legacyRedirects";

const enableRUMMeta = { enableRUM: true };

// ==========================================
// Standard Flow Routes (/:countryCode/store/*)
// ==========================================
const standardRoutes: Array<RouteRecordRaw> = [
	{
		path: "/store/merchant-login",
		name: RouteName.M3MerchantLogin,
		component: MerchantLoginView,
		beforeEnter: [navigationGuardForMerchant, merchant3TranslationGuard],
	},
	{
		path: "/store/request-reset-password",
		name: RouteName.M3RMerchantRequestResetPassword,
		component: MerchantRequestResetPasswordView,
		beforeEnter: [merchant3TranslationGuard],
	},
	{
		path: "/store/recreate-password",
		name: RouteName.M3RMerchantRecreatePassword,
		component: MerchantRecreatePasswordView,
		beforeEnter: [merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/clerk-login",
		name: RouteName.M3ClerkLogin,
		component: ClerkLoginView,
		beforeEnter: [navigationGuardForClerk, merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/:clerkId/face-login",
		name: RouteName.M3FaceLogin,
		component: FaceLoginView,
		beforeEnter: [merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/:clerkId/take-selfie",
		name: RouteName.M3TakeASelfieIntro,
		component: TakeASelfieIntroView,
		beforeEnter: [merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/qr-login",
		name: RouteName.M3QRLogin,
		component: QRCodeLoginView,
		beforeEnter: [merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/device-login",
		name: RouteName.M3DeviceLogin,
		component: DeviceLoginView,
		beforeEnter: [merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/qr",
		name: RouteName.M3QrLanding,
		component: QRScanLanding,
		beforeEnter: [merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/welcome",
		name: RouteName.M3Welcome,
		component: WelcomeView,
		beforeEnter: [navigationGuardWelcome, merchant3TranslationGuard],
		meta: enableRUMMeta,
	},
	{
		path: "/:countryCode/store/device-repair-list",
		name: RouteName.M3DeviceRepairList,
		component: DeviceRepairListView,
		beforeEnter: [navigationGuardWelcome, merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/device-repair-process/:financeOrderId",
		name: RouteName.M3DeviceRepairProcess,
		component: DeviceRepairProcessView,
		beforeEnter: [navigationGuardWelcome, merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/create-sales-clerk",
		name: RouteName.M3CreateSalesClerk,
		component: CreateSalesClerkView,
		beforeEnter: [navigationGuardWelcome, merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/help",
		name: RouteName.M3Help,
		component: HelpView,
		beforeEnter: [navigationGuardWelcome, merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/payments/accept-payment",
		name: RouteName.M3PaymentsInquery,
		component: PaymentInqueryView,
		beforeEnter: [navigationGuardWelcome, merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/payments/collect-payment",
		name: RouteName.M3PaymentsInqueryResult,
		component: PaymentInqueryResultView,
		beforeEnter: [navigationGuardInStorePayment, merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/payments/receipt",
		name: RouteName.M3PaymentsReceipt,
		component: PaymentsReceiptView,
		beforeEnter: [navigationGuardInStorePayment, merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/pre-registration-offers",
		name: RouteName.M3PreRegistrationOffers,
		component: PreRegistrationOffersView,
		beforeEnter: [
			navigationGuard,
			navigationGuardForCheckOut,
			merchant3TranslationGuard,
		],
	},
	{
		path: "/:countryCode/store/offers",
		name: RouteName.M3Offers,
		component: OffersView,
		beforeEnter: [
			navigationGuard,
			navigationGuardForCheckOut,
			merchant3TranslationGuard,
		],
	},
	{
		path: "/:countryCode/store/imei",
		name: RouteName.M3Imei,
		component: ImeiView,
		beforeEnter: [
			navigationGuard,
			navigationGuardForCheckOut,
			merchant3TranslationGuard,
		],
	},
	{
		path: "/:countryCode/store/receipt/:financeOrderId",
		name: RouteName.M3Receipt,
		component: ReceiptView,
		beforeEnter: [
			navigationGuard,
			navigationGuardForCheckOut,
			merchant3TranslationGuard,
		],
	},
	{
		path: "/:countryCode/store/contract",
		name: RouteName.M3Contract,
		component: ContractView,
		beforeEnter: [
			navigationGuard,
			navigationGuardForCheckOut,
			merchant3TranslationGuard,
		],
	},
	{
		path: "/:countryCode/store/cart-verification",
		name: RouteName.M3CartVerification,
		component: CartVerificationView,
		meta: {
			docTitleTranslationKey: "CART_VERIFICATION.PAGE_TITLE",
		},
		beforeEnter: [
			navigationGuard,
			navigationGuardForCheckOut,
			merchant3TranslationGuard,
		],
	},
	{
		path: "/:countryCode/store/software",
		name: RouteName.M3Software,
		component: SoftwareInstallView,
		beforeEnter: [
			navigationGuard,
			navigationGuardForCheckOut,
			merchant3TranslationGuard,
		],
	},
	{
		path: "/:countryCode/store/software-install-failed",
		name: RouteName.M3SoftwareInstallFailed,
		component: SoftwareInstallFailedView,
		beforeEnter: [
			navigationGuard,
			navigationGuardForCheckOut,
			merchant3TranslationGuard,
		],
	},
	{
		path: "/:countryCode/store/electronicdownpaymentstatus",
		name: RouteName.M3ElectronicDownPaymentStatus,
		component: ElectronicDownPaymentStatusView,
		beforeEnter: [
			navigationGuard,
			navigationGuardForCheckOut,
			merchant3TranslationGuard,
		],
	},
	{
		path: "/:countryCode/store/complete",
		name: RouteName.M3Complete,
		component: CompleteView,
		beforeEnter: [
			navigationGuard,
			navigationGuardForCheckOut,
			merchant3TranslationGuard,
		],
	},
	{
		path: "/:countryCode/store/customer-registration",
		name: RouteName.M3CustomerRegistration,
		component: CustomerRegistrationView,
		beforeEnter: [
			navigationGuard,
			navigationGuardForCheckOut,
			merchant3TranslationGuard,
		],
		meta: enableRUMMeta,
	},
	{
		path: "/:countryCode/store/post-offers-registration",
		name: RouteName.M3PostOfferRegistration,
		component: PostOfferRegistrationView,
		beforeEnter: [
			navigationGuard,
			navigationGuardForCheckOut,
			merchant3TranslationGuard,
		],
	},
	{
		path: "/:countryCode/store/carts/:cartTag/incode",
		name: RouteName.M3IncodeCaptureFront,
		component: IncodeCapture,
		meta: enableRUMMeta,
		beforeEnter: [merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/clerk/:clerkId/incode",
		name: RouteName.M3FaceLoginIncodeCapture,
		component: FaceLoginView,
		beforeEnter: [merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/clerk/register",
		name: RouteName.M3RegisterClerk,
		component: ClerkRegistrationView,
		beforeEnter: [merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/clerk/:clerkId/qr-login/incode",
		name: RouteName.M3QrLoginIncodeCapture,
		component: QrLoginIncodeCaptureView,
		beforeEnter: [merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/customer-registration/terms-conditions",
		name: RouteName.M3TermsAndConditions,
		component: TermsAndConditionsView,
		beforeEnter: [navigationGuard, merchant3TranslationGuard],
		meta: enableRUMMeta,
	},
	{
		path: "/:countryCode/store/customer-registration/date-of-birth-verification",
		name: RouteName.M3DateOfBirthVerification,
		component: CustomerDateOfBirthView,
		beforeEnter: [navigationGuard, merchant3TranslationGuard],
		meta: enableRUMMeta,
	},
	{
		path: "/:countryCode/store/customer-registration/id-number-dob-verification",
		name: RouteName.M3IdNumberDobVerification,
		component: CustomerIdNumberView,
		beforeEnter: [navigationGuard, merchant3TranslationGuard],
		meta: enableRUMMeta,
	},
	{
		path: "/:countryCode/store/customer-registration/ask-cx-call",
		name: RouteName.M3SelfOnboardingAskCxCall,
		component: AskCxCallView,
		beforeEnter: [
			navigationGuard,
			navigationGuardForCheckOut,
			merchant3TranslationGuard,
		],
		meta: enableRUMMeta,
	},
	{
		path: "/:countryCode/store/customer-registration/id-uploader",
		name: RouteName.M3IdentificationUploader,
		component: IdentificationUploaderView,
		beforeEnter: [
			navigationGuard,
			navigationGuardForCheckOut,
			merchant3TranslationGuard,
		],
		meta: enableRUMMeta,
	},
	{
		// External uploader doesn't follow the standard pattern as it's accessed externally
		path: "/customer-registration/:userTag/external-uploader",
		name: RouteName.M3ExternalIdUploader,
		component: ExternalIdUploader,
		beforeEnter: [merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/customer-registration/credit-check-permission",
		name: RouteName.M3CreditCheckPermissionView,
		component: CreditCheckPermissionView,
		beforeEnter: [
			navigationGuard,
			navigationGuardForCheckOut,
			merchant3TranslationGuard,
		],
		meta: enableRUMMeta,
	},
	{
		path: "/:countryCode/store/customer-registration/customer-contact-info",
		name: RouteName.M3CustomerContactInfoView,
		component: CustomerContactInfoView,
		beforeEnter: [
			navigationGuard,
			navigationGuardForCheckOut,
			merchant3TranslationGuard,
		],
		meta: enableRUMMeta,
	},
	{
		path: "/:countryCode/store/customer-registration/:userTag/wait-validation",
		name: RouteName.M3CXWaitValidationView,
		component: WaitValidationView,
		beforeEnter: [
			navigationGuard,
			navigationGuardForCheckOut,
			merchant3TranslationGuard,
		],
		meta: enableRUMMeta,
	},
	{
		path: "/:countryCode/store/customer-registration/declined-validation",
		name: RouteName.M3CustomerDeclinedValidation,
		component: DeclinedValidationView,
		beforeEnter: [navigationGuard, merchant3TranslationGuard],
		meta: enableRUMMeta,
	},
	{
		path: "/:countryCode/store/supported-models",
		name: RouteName.M3SupportedModels,
		component: SupportedModelsView,
		beforeEnter: [navigationGuardWelcome, merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/latest-receipts",
		name: RouteName.M3LatestReceipts,
		component: LatestMerchantReceiptsView,
		beforeEnter: [navigationGuardWelcome, merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/merchant-sale-receipt/:financeOrderId?/:creditLineId?",
		name: RouteName.M3MerchantSaleReceipt,
		component: MerchantSaleReceiptView,
		beforeEnter: [navigationGuardWelcome, merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/merchant-payment-receipt/:paymentId",
		name: RouteName.M3MerchantPaymentReceipt,
		component: MerchantPaymentReceiptView,
		beforeEnter: [navigationGuardWelcome, merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/speed-money",
		name: RouteName.M3SpeedMoney,
		component: SpeedMoneyView,
		beforeEnter: [navigationGuardWelcome, merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/incode",
		name: RouteName.M3IncodeWebFlow,
		component: IncodeFlowView,
		beforeEnter: [
			navigationGuard,
			navigationGuardForCheckOut,
			merchant3TranslationGuard,
		],
		meta: enableRUMMeta,
	},
	{
		path: "/:countryCode/store/incode-introduction",
		name: RouteName.M3IncodeIntroduction,
		component: IncodeIntroductionView,
		beforeEnter: [
			navigationGuard,
			navigationGuardForCheckOut,
			merchant3TranslationGuard,
		],
		meta: enableRUMMeta,
	},
	{
		path: "/:countryCode/store/speed-money/register",
		name: "M3_SpeedMoney_Register",
		component: SpeedMoneyRegisterView,
		beforeEnter: [navigationGuardWelcome, merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/speed-money/search-existing-registration",
		name: "M3_SpeedMoney_Search_Existing_Registration",
		component: SpeedMoneySearchExistingRegistrationView,
		beforeEnter: [navigationGuardWelcome, merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/speed-money/edit",
		name: "M3_SpeedMoney_Edit",
		component: SpeedMoneyEditView,
		beforeEnter: [navigationGuardWelcome, merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/PreInstallDevicesView",
		name: RouteName.M3EnrollDevice,
		component: PreInstallDevicesView,
		beforeEnter: [navigationGuardWelcome, merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/v2/offers/preparing",
		name: RouteName.M3PreparingOffersV2,
		component: PreparingOffersView,
		beforeEnter: [merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/v2/offers/select",
		name: RouteName.M3SelectingOffersV2,
		component: OffersV2View,
		beforeEnter: [merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/add-ons",
		name: RouteName.M3AddOns,
		component: AddOnsView,
		beforeEnter: [merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/store/purchase-confirmation",
		name: RouteName.M3PurchaseConfirmation,
		component: PurchaseConfirmationView,
		meta: {
			docTitleTranslationKey: "PAGE_TITLE_PURCHASE_CONFIRMATION",
		},
		beforeEnter: [navigationGuardWelcome, merchant3TranslationGuard],
	},
];

// ==========================================
// Ecommerce Flow Routes (/:countryCode/ecommerce/*)
// ==========================================
const ecommerceRoutes: Array<RouteRecordRaw> = [
	{
		path: "/:countryCode/ecommerce/start",
		name: RouteName.M3Ecommerce,
		component: EcommerceView,
		meta: enableRUMMeta,
		beforeEnter: [merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/ecommerce/rejected",
		name: RouteName.M3RejectedOfferEcommerce,
		component: RejectedOfferEcommerceView,
		beforeEnter: [navigationGuardWelcome, merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/ecommerce/summary",
		name: RouteName.M3EcommerceSummary,
		component: EcommerceSummaryView,
		beforeEnter: [navigationGuardWelcome, merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/ecommerce/selectDownpaymentType",
		name: RouteName.M3EcommerceSelectDownpaymentType,
		component: EcommerceSelectDownpaymentTypeView,
		beforeEnter: [navigationGuardWelcome, merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/ecommerce/card-ui-redirect",
		name: RouteName.M3EcommerceOpenPayDownPaymentAltUi,
		component: OpenPayCardUiView,
		beforeEnter: [navigationGuardWelcome, merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/ecommerce/cashInStoreDownpayment",
		name: RouteName.M3EcommerceCashInStoreDownpayment,
		component: EcommerceCashInStoreDownpaymentView,
		beforeEnter: [navigationGuardWelcome, merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/ecommerce/openpay-down-payment",
		name: RouteName.M3EcommerceOpenPayDownPayment,
		component: EcommerceOpenPayDownPaymentView,
		beforeEnter: [navigationGuardWelcome, merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/ecommerce/down-payment-paid",
		name: RouteName.M3EcommerceDownPaymentPaid,
		component: EcommerceDownPaymentPaidView,
		beforeEnter: [merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/ecommerce/partner-down-payment",
		name: RouteName.M3EcommercePartnerDownPayment,
		component: EcommercePartnerDownPaymentView,
		beforeEnter: [merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/ecommerce/pix-down-payment",
		name: RouteName.M3EcommercePixDownPayment,
		component: EcommercePixDownPaymentView,
		beforeEnter: [navigationGuardWelcome, merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/ecommerce/finished-sale",
		name: RouteName.M3EcommerceFinishedSale,
		component: EcommerceFinishedSaleView,
		beforeEnter: [merchant3TranslationGuard],
	},
];

// ==========================================
// Kiosk Flow Routes (/:countryCode/kiosk/*)
// Self-onboarding / Legacy 2-step flow
// ==========================================
const kioskRoutes: Array<RouteRecordRaw> = [
	{
		path: "/:countryCode/kiosk/instructions",
		name: RouteName.M3SelfOnboardingInstructions,
		component: SelfOnboardingInstructionsView,
		beforeEnter: [navigationGuardWelcome, merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/kiosk/summary",
		name: RouteName.M3SelfOnboardingSummary,
		component: SelfOnboardingSummaryView,
		beforeEnter: [navigationGuardWelcome, merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/kiosk/get-valid-cart",
		name: RouteName.M3SelfOnboardingGetValidCart,
		component: FinishSaleView,
		beforeEnter: [navigationGuardWelcome, merchant3TranslationGuard],
	},
	{
		path: "/:countryCode/kiosk/cart-details",
		name: RouteName.M3SelfOnboardingCartDetails,
		component: FinishSaleCartDetailsView,
		beforeEnter: [navigationGuard, merchant3TranslationGuard],
	},
];

// ==========================================
// Combined Routes
// ==========================================
const routes: Array<RouteRecordRaw> = [
	// New structured routes (/:countryCode/:flow/*)
	...standardRoutes,
	...ecommerceRoutes,
	...kioskRoutes,
	...directToCustomerRoutes,
	...configurableRoutes,
	// Legacy redirects for backwards compatibility
	...legacyRedirects,
];

export default routes;
